import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  T_CreateOrderResponse,
  T_HLSlice,
  T_ReKycData,
} from "./Homeloan_Types";
import { T_BackgroundImage } from "../../globalTypes/GlobalTypes";

export const getHomeLoanPaymentInfoInitialData = (): T_CreateOrderResponse => {
  return {
    payment_data: {
      pl_order_id: "",
      merc_order_id: "",
      merc_order_date: "",
      merc_id: "",
      amount: "",
      return_url: "",
      product_type: "",
      product_id: "",
      customer_id: "",
      customer_name: "",
      customer_phone: "",
      order_desc: "",
      udf1: "",
      udf2: "",
      account_no: "",
      account_ifsc: "",
    },
    order_data: {
      pl_order_id: "",
      created_date: "",
      status: "",
      status_code: "",
      links: {
        payment_link_web: "",
      },
    },
  };
};

const initialState: T_HLSlice = {
  summaryData: [],
  secondaryUserSummaryData: [],
  loanInfo: {
    totalRows: "0",
    moreRows: "false",
    rows: [],
  },
  secondaryUserLoanInfo: {
    totalRows: "0",
    moreRows: "false",
    rows: [],
  },
  paymentInfo: getHomeLoanPaymentInfoInitialData(),
  serviceMenuList: [],
  serviceReqList: [],
  tenureOptionSelected: "",
  advanceEMIAmount: "",
  countDetails: [],
  hlEmailClickCount: {} as Record<string, Record<string, number>>,
  hlRoiHistory: [],
  backgroundImages: [],
  pddInformation: null,
  homeLoanIdentifierData: [],
  isReKycPopupDismissed: false,
  shouldOpenMyServicesPopup: false,
  shouldOpenServiceRequests: false,
  reKycData: null,
};

export const HomeLoanSlice = createSlice({
  name: "HomeLoanSlice",
  initialState,
  reducers: {
    setSummaryData: (state, action) => {
      return {
        ...state,
        summaryData: action.payload,
      };
    },
    setSecondaryUserSummaryData: (state, action) => {
      return {
        ...state,
        secondaryUserSummaryData: action.payload,
      };
    },
    setHomeLoanInfo: (state, action) => {
      return {
        ...state,
        loanInfo: action.payload,
      };
    },
    setSecondaryUserHomeLoanInfo: (state, action) => {
      return {
        ...state,
        secondaryUserLoanInfo: action.payload,
      };
    },
    setEmiPaymentOrderInfo: (state, action) => {
      return {
        ...state,
        paymentInfo: action.payload,
      };
    },
    setServiceDataMenuList: (state, action) => {
      return {
        ...state,
        serviceMenuList: action.payload,
      };
    },
    setServiceRequestsList: (state, action) => {
      return {
        ...state,
        serviceReqList: action.payload,
      };
    },
    setEMITenureOptionSelected: (state, action) => {
      return {
        ...state,
        tenureOptionSelected: action.payload,
      };
    },
    setHlCountDetails: (state, action) => {
      return {
        ...state,
        countDetails: [...state.countDetails, ...action.payload],
      };
    },
    setHlEmailClickCount: (state, action) => {
      const { loanAccountNumber, featureName, count } = action.payload;
      if (!state.hlEmailClickCount[loanAccountNumber]) {
        state.hlEmailClickCount[loanAccountNumber] = {};
      }
      state.hlEmailClickCount[loanAccountNumber][featureName] = count;
    },
    updateHlEmailClickCount: (
      state,
      action: PayloadAction<{ loanAccountNumber: string; featureName: string }>,
    ) => {
      const { loanAccountNumber, featureName } = action.payload;
      if (!state.hlEmailClickCount[loanAccountNumber]) {
        state.hlEmailClickCount[loanAccountNumber] = {};
      }
      const current =
        state.hlEmailClickCount[loanAccountNumber][featureName] ??
        state.countDetails
          ?.find((item) => item.loanAccountNumber === loanAccountNumber)
          ?.countDetails?.find((c) => c.functionalityCode === featureName)
          ?.entryCount ??
        0;
      state.hlEmailClickCount[loanAccountNumber][featureName] = current + 1;
    },
    setAdvanceEMIAmount: (state, action) => {
      return {
        ...state,
        advanceEMIAmount: action.payload,
      };
    },
    setHlRoiHistory: (state, action) => {
      return {
        ...state,
        hlRoiHistory: action.payload,
      };
    },
    setPddInformation: (state, action) => {
      return {
        ...state,
        pddInformation: action.payload,
      };
    },
    updatePddInformation: (state, action) => {
      return {
        ...state,
        pddInformation: { ...state?.pddInformation, ...action.payload },
      };
    },
    setHlBackgroundImage: (state, action: PayloadAction<T_BackgroundImage>) => {
      const existingBackgroundImages = Array.isArray(state.backgroundImages)
        ? state.backgroundImages
        : [];

      return {
        ...state,
        backgroundImages: [...existingBackgroundImages, action.payload],
      };
    },
    setHomeLoanIdentifierData: (state, action) => {
      return {
        ...state,
        homeLoanIdentifierData: action.payload,
      };
    },
    setIsReKycPopupDismissed: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        isReKycPopupDismissed: action.payload,
      };
    },
    setShouldOpenMyServicesPopup: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        shouldOpenMyServicesPopup: action.payload,
      };
    },
    setShouldOpenServiceRequests: (state, action: PayloadAction<boolean>) => {
      return {
        ...state,
        shouldOpenServiceRequests: action.payload,
      };
    },
    updateReKycData: (state, action: PayloadAction<T_ReKycData>) => {
      return {
        ...state,
        reKycData: { ...state.reKycData, ...action.payload },
      };
    },
    deleteReKycCase: (state) => {
      return {
        ...state,
        reKycData: null,
      };
    },
  },
});

export const HomeLoanReducer = HomeLoanSlice.reducer;
export const HomeLoanSliceSliceKey = HomeLoanSlice.reducerPath;
export const {
  setSummaryData,
  setSecondaryUserSummaryData,
  setHomeLoanInfo,
  setEmiPaymentOrderInfo,
  setServiceDataMenuList,
  setServiceRequestsList,
  setSecondaryUserHomeLoanInfo,
  setEMITenureOptionSelected,
  setHlCountDetails,
  setHlEmailClickCount,
  updateHlEmailClickCount,
  setAdvanceEMIAmount,
  setHlRoiHistory,
  setPddInformation,
  updatePddInformation,
  setHlBackgroundImage,
  setHomeLoanIdentifierData,
  setIsReKycPopupDismissed,
  setShouldOpenMyServicesPopup,
  setShouldOpenServiceRequests,
  updateReKycData,
  deleteReKycCase,
} = HomeLoanSlice.actions;
